import * as React from 'react';
import { useIsomorphicEffect } from './useIsomorphicEffect';

export function useMediaQuery(
    query: string,
    // eslint-disable-next-line no-shadow
    options: { ssrMatchMedia?: (query: string) => MediaQueryList; noSsr?: boolean; defaultMatches?: boolean } = {}
): boolean {
    const { ssrMatchMedia = null, noSsr = false, defaultMatches = false } = options;
    const matchMediaSupported = typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';

    const [match, setMatch] = React.useState(() => {
        if (noSsr && matchMediaSupported) {
            return matchMedia(query).matches;
        }
        if (ssrMatchMedia) {
            // This is a custom match media implementation with predicted values that will be used for both
            // the server side render and the initial client side render, ensuring a hydration match.
            // Subsequent renders will be handled by useIsomorphicEffect using the browser implementation.
            return ssrMatchMedia(query).matches;
        }
        return defaultMatches;
    });

    useIsomorphicEffect(() => {
        let active = true;

        if (!matchMediaSupported) {
            return undefined;
        }

        const queryList = matchMedia(query);

        const updateMatch = (): void => {
            if (active) {
                setMatch(queryList.matches);
            }
        };

        updateMatch();

        queryList.addListener(updateMatch);
        return () => {
            active = false;
            queryList.removeListener(updateMatch);
        };
    }, [query, matchMediaSupported]);

    return match;
}
